import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n';
import { homePath, legalPath, sectionPath } from '../seo/routes';
import { SOCIAL_LINKS, NAV_ITEMS } from '../constants';
import { GithubIcon, LinkedinIcon, MailIcon } from './icons';

export const Footer = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t border-border mt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 clip-chamfer bg-primary flex items-center justify-center text-primary-foreground font-display font-bold text-sm">
                RS
              </div>
              <h3 className="font-semibold">{t('footer.title')}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('footer.description')}</p>
            <div className="flex gap-3">
              <a
                href={SOCIAL_LINKS.github}
                target="_blank"
                rel="noreferrer"
                className="glyph-chip h-9 w-9 text-muted-foreground transition-colors hover:text-primary"
                aria-label="GitHub"
              >
                <span><GithubIcon className="h-4 w-4" /></span>
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noreferrer"
                className="glyph-chip h-9 w-9 text-muted-foreground transition-colors hover:text-primary"
                aria-label="LinkedIn"
              >
                <span><LinkedinIcon className="h-4 w-4" /></span>
              </a>
              <a
                href={sectionPath(locale, 'contact')}
                className="glyph-chip h-9 w-9 text-muted-foreground transition-colors hover:text-primary"
                aria-label={t('footer.contactForm')}
              >
                <span><MailIcon className="h-4 w-4" /></span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={homePath(locale)} viewTransition className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('common.home')}
                </Link>
              </li>
              {NAV_ITEMS.map((item) => (
                <li key={item.hash}>
                  <a
                    href={sectionPath(locale, item.hash)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t(item.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-8 flex flex-col items-start gap-4 text-sm text-muted-foreground lg:flex-row lg:items-center lg:justify-between">
          <p>
            &copy; {currentYear} {t('footer.title')}. {t('footer.copyright')}
          </p>
          <nav className="flex items-center gap-4 whitespace-nowrap" aria-label={t('footer.legal.label')}>
            <Link to={legalPath(locale, 'impressum')} className="transition-colors hover:text-primary">{t('footer.legal.impressum')}</Link>
            <span aria-hidden="true">·</span>
            <Link to={legalPath(locale, 'datenschutz')} className="transition-colors hover:text-primary">{t('footer.legal.privacy')}</Link>
          </nav>
          <p className="font-mono text-xs tracking-wide">
            <span className="text-primary">BUILD OK</span> <span aria-hidden="true">·</span> EXIT 0 <span aria-hidden="true">·</span> React + Tailwind on Cloudflare
          </p>
        </div>
      </div>
    </footer>
  );
};
